// Shared 2x building parts (windows, doors, awnings, signs...) for the front painters in hd-fronts-*.ts.
// Region files import what they need from here; add new shared parts here only when two regions use them.
import { hash2 } from '../../common/src/util';
import { K, NEON, type NeonColor, type G, px, speckle, dither, bands, disc, frame, shadow, dk, lt, alpha, text, textW, glyph, glyphW } from './hd-kit';
import type { LightFn } from './hd-registry';

// ------------------------------------------------------------------ façade parts
export function cornice(g: G, x: number, y: number, w: number) {
  px(g, x, y, w, 2, K.ink); px(g, x, y + 2, w, 3, K.concreteHi); px(g, x, y + 5, w, 1, K.concreteLo); px(g, x, y + 6, w, 3, alpha(K.night, 0.45));
}
export function windowPane(g: G, x: number, y: number, w: number, h: number, state: number, L: LightFn) {
  px(g, x - 1, y - 1, w + 2, h + 2, K.ink);
  if (state < 0.45) { bands(g, x, y, w, h, K.warmHi, K.warm, 3); px(g, x, y + 2, w, 1, alpha(K.night, 0.35)); px(g, x + Math.floor(w / 2), y, 1, h, K.warmLo); L(x, y, w, h, K.amber, 0.14); }
  else if (state < 0.6) { bands(g, x, y, w, h, K.glassHi, K.glass, 2); px(g, x + Math.floor(w / 2), y, 1, h, K.glassLo); L(x, y, w, h, K.cyan, 0.1); }
  else if (state < 0.75) { px(g, x, y, w, h, K.warmDim); px(g, x, y, w, Math.ceil(h / 2), K.warmLo); }
  else { px(g, x, y, w, h, K.glassLo); px(g, x + 1, y + 1, 2, 1, K.glass); }
  px(g, x - 1, y + h + 1, w + 2, 1, K.concreteHi); // sill
}
/** a lit shop window seen from the street: shelves, goods and a glass glint */
export function display(g: G, x: number, y: number, w: number, h: number, tone: string, L: LightFn, goods = true) {
  px(g, x - 2, y - 2, w + 4, h + 4, K.ink); px(g, x - 1, y - 1, w + 2, h + 2, K.metalLo);
  bands(g, x, y, w, h, lt(tone, 0.55), dk(tone, 0.2), 4);
  if (goods) for (let j = 6; j < h - 3; j += 9) {
    px(g, x, y + j, w, 1, dk(tone, 0.55));
    for (let i = 2; i < w - 2; i += 3) { const v = hash2(x + i, y + j); px(g, x + i, y + j - 3, 2, 3, v < 0.33 ? K.pink : v < 0.66 ? K.cyan : K.warmLo); }
  }
  for (let k = 0; k < h; k++) px(g, x + Math.floor(w * 0.6) + Math.floor(k / 2), y + k, 2, 1, alpha(K.white, 0.18)); // glint
  for (let i = Math.floor(w / 3); i < w; i += Math.floor(w / 3)) px(g, x + i, y, 1, h, K.metalLo); // mullions
  L(x, y, w, h, tone, 0.35, true);
}
/** a recessed doorway: shaded reveal, lit glass door, bright threshold */
export function door(g: G, x: number, y: number, w: number, h: number, inner: string, L: LightFn) {
  px(g, x - 2, y - 2, w + 4, h + 2, K.ink);
  px(g, x, y, w, h, dk(inner, 0.6)); px(g, x, y, w, 5, alpha(K.night, 0.6)); px(g, x, y, 3, h, alpha(K.night, 0.5));
  bands(g, x + 4, y + 6, w - 8, h - 8, lt(inner, 0.35), dk(inner, 0.15), 3); px(g, x + Math.floor(w / 2), y + 6, 1, h - 8, K.ink);
  px(g, x + Math.floor(w / 2) - 3, y + Math.floor(h / 2), 2, 4, K.metalHi); px(g, x + Math.floor(w / 2) + 2, y + Math.floor(h / 2), 2, 4, K.metalHi);
  px(g, x - 2, y + h - 2, w + 4, 2, lt(inner, 0.3));
  L(x, y, w, h, inner, 0.4, true);
}
export function awning(g: G, x: number, y: number, w: number, h: number, a: string, b: string) {
  for (let i = 0; i < w; i += 12) { px(g, x + i, y, Math.min(6, w - i), h, a); px(g, x + i + 6, y, Math.min(6, w - i - 6), h, b); }
  px(g, x, y, w, 2, alpha(K.night, 0.35)); px(g, x, y + Math.floor(h / 2), w, 1, alpha(K.white, 0.12));
  for (let i = 0; i < w; i += 6) px(g, x + i + 1, y + h, 4, 2, (i / 6) % 2 ? b : a); // scalloped valance
  px(g, x, y + h + 2, w, 4, alpha(K.night, 0.4)); // its shadow on the wall
  frame(g, x - 1, y - 1, w + 2, h + 2, alpha(K.night, 0.6));
}
export function lantern(g: G, x: number, y: number, col: string, L: LightFn) {
  px(g, x + 3, y - 2, 2, 2, K.ink);
  px(g, x + 1, y, 6, 2, K.ink); px(g, x, y + 2, 8, 9, col); px(g, x + 1, y + 11, 6, 2, K.ink);
  for (let j = 4; j < 11; j += 2) px(g, x, y + j, 8, 1, dk(col, 0.35));
  px(g, x + 1, y + 2, 2, 8, lt(col, 0.5)); px(g, x + 3, y + 5, 2, 3, lt(col, 0.7));
  L(x - 2, y, 12, 13, col, 0.5, true);
}
export function vending(g: G, x: number, y: number, body: string, L: LightFn) {
  px(g, x, y, 20, 36, K.ink); px(g, x + 1, y + 1, 18, 34, body); px(g, x + 1, y + 1, 18, 2, lt(body, 0.4)); px(g, x + 17, y + 3, 2, 32, dk(body, 0.35));
  px(g, x + 3, y + 5, 14, 15, lt(K.cyan, 0.75));
  for (let j = 0; j < 3; j++) for (let i = 0; i < 4; i++) { const v = hash2(x + i, j + y); px(g, x + 4 + i * 3, y + 7 + j * 5, 2, 3, v < 0.3 ? K.pink : v < 0.6 ? K.cyan : v < 0.85 ? K.amber : K.white); }
  px(g, x + 3, y + 23, 9, 3, K.ink); px(g, x + 14, y + 22, 3, 5, K.metalLo); px(g, x + 3, y + 29, 14, 4, K.ink);
  L(x + 2, y + 4, 16, 17, K.cyan, 0.32, true);
}
export function plant(g: G, x: number, y: number) {
  px(g, x, y + 10, 12, 9, K.ink); px(g, x + 1, y + 11, 10, 7, K.concreteLo); px(g, x + 1, y + 11, 10, 1, K.concreteHi);
  disc(g, x + 6, y + 6, 6, 6, K.leafLo); disc(g, x + 5, y + 5, 4, 4, K.leaf); px(g, x + 3, y + 2, 3, 2, K.leafHi);
}
export function wallAc(g: G, x: number, y: number) {
  shadow(g, x + 2, y + 3, 18, 13); px(g, x, y, 18, 12, K.ink); px(g, x + 1, y + 1, 16, 10, K.metalHi); disc(g, x + 6, y + 6, 3, 3, K.metalLo); for (let i = 11; i < 16; i += 2) px(g, x + i, y + 2, 1, 8, K.metal);
}
/** glyph scale 3 when the sign fits its façade, else 2 */
export const fitS = (txt: string, w: number) => (textW(txt, 3) + 12 <= w - 12 ? 3 : 2);
export function bandSign(g: G, cx: number, y: number, txt: string, color: NeonColor, L: LightFn, s = 2) {
  const col = NEON[color], tw = textW(txt, s), w = tw + 12, h = 6 * s + 10, x = cx - Math.floor(w / 2);
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(col, 0.82));
  frame(g, x + 2, y + 2, w - 4, h - 4, col); frame(g, x + 3, y + 3, w - 6, h - 6, dk(col, 0.4));
  text(g, txt, x + 6, y + 5, lt(col, 0.6), s);
  L(x, y, w, h, col, 0.6, true, true);
  return w;
}
export function bladeSign(g: G, x: number, y: number, txt: string, color: NeonColor, L: LightFn, s = 2) {
  const col = NEON[color], chars = [...txt].map(c => (c === 'ー' ? '｜' : c)), cw = Math.max(...chars.map(glyphW)) * s, w = cw + 10, h = chars.length * (6 * s + 3) + 8;
  px(g, x - 3, y + 6, 3, 2, K.metalLo); px(g, x - 3, y + h - 8, 3, 2, K.metalLo); // brackets to the wall
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(col, 0.8)); frame(g, x + 2, y + 2, w - 4, h - 4, col);
  chars.forEach((c, i) => glyph(g, c, x + 5 + Math.floor((cw - glyphW(c) * s) / 2), y + 5 + i * (6 * s + 3), lt(col, 0.6), s));
  L(x, y, w, h, col, 0.6, true, true);
  return h;
}
